import React, {
  useMemo,
  Fragment,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Loading from 'components/Loading'
import Divider from '../../../Divider'
import Text from '../../../Text'
import { Button } from 'semantic-ui-react'
import { formatDateTime } from '@enlyt/helpers/parse'
import useMediaQuery from 'hooks/useMediaQuery'
import { useModal } from 'src/hooks/useModal'
import queryParams, { getPageParamsFromUrl } from 'helpers/urlhelper'
import _ from 'lodash'
import localization from './localization'
import themeStyles from './styles'
import { useTheme } from 'contexts/ThemeProvider'
import NoData from '../../../MyHealthContentArea/NoDataAvailableBar'
import LoadMoreButton from 'components/LoadMoreButton'
import FhirEnhancedActions from '@web/stores/FhirEnhanced/Actions'
import {
  radiologyBinarySearchLoading,
  radiologyBinary,
  radiologySearchNextLoading,
  radiologyBinaryCache,
  radiologyBinaryPreviewLoading,
  radiologyBinarySearchError
} from '@web/stores/FhirEnhanced/Select'
import { getCurPatientId } from '@web/stores/Account/Select'
import { isIOSSafari, base64ToUri } from '@web/helpers/downloadPdfToFileSystem'
import { sortRadiology } from '@web/helpers/sortBy'
import SthWentWrong from 'containers/MyHealth/components/MyHealthContentArea/SthWentWrong'
import withLocalization from '@web/helpers/localizationUtils'

import DownIcon from '@images/custom/icons/download.svg'

import { ErrorModal } from 'src/components/ModalTemplates'
import { IconErrorMessages } from '@images/svg'
import getDownloadErrorMessage from '@web/helpers/getDownloadErrorMessage'

const binarySearch = {
  name: 'radiology',
  request: FhirEnhancedActions.radiologyBinarySearch,
  preview: FhirEnhancedActions.radiologyBinaryPreview,
  loading: radiologyBinarySearchLoading,
  previewLoading: radiologyBinaryPreviewLoading,
  json: radiologyBinary,
  cache: radiologyBinaryCache,
  error: radiologyBinarySearchError,
  cancelPreview: FhirEnhancedActions.radiologyBinaryPreviewCancel
}

const RadiologyFragmentModule = ({ items, binarySearch, needSubtitle, t }) => {
  const { isMobile } = useMediaQuery()

  const { theme } = useTheme()
  const styles = themeStyles(theme)

  const dispatch = useDispatch()
  const [fileId, setFileId] = useState(null)
  const [needDownload, setNeedDownload] = useState(false)
  const [currBinary, setCurrBinary] = useState('')
  const binaryLoading = useSelector(binarySearch.loading)
  const binaryPreviewLoading = useSelector(binarySearch.previewLoading)
  const request = binarySearch.request
  const preview = binarySearch.preview
  const binaryCache = useSelector(binarySearch.cache)
  const binaryError = useSelector(binarySearch.error)
  const cancelPreview = binarySearch.cancelPreview
  const mobileSafari = isIOSSafari()
  const { show: showErrorModalContent } = useModal()

  // Tracks the PDF preview tab to fix blocking loading at documents.
  const previewTabRef = useRef(null)
  const previewPollRef = useRef(null)
  const isPreviewLoadingRef = useRef(false)

  useEffect(() => {
    isPreviewLoadingRef.current = binaryPreviewLoading
  }, [binaryPreviewLoading])

  useEffect(() => {
    return () => {
      if (previewPollRef.current) {
        clearInterval(previewPollRef.current)
      }
    }
  }, [])

  useEffect(
    () => {
      if (binaryLoading || binaryPreviewLoading || !needDownload) {
        return
      }
      const pdfBinary = currBinary || _.get(binaryCache, fileId, null)
      if (pdfBinary && fileId) {
        try {
          const content = `data:application/pdf;base64,${pdfBinary}`
          const uri = mobileSafari ? base64ToUri(pdfBinary) : content
          // handle download pdf
          if (fileRef.current) {
            fileRef.current.href = uri
            fileRef.current.download = `${fileId}.pdf`
            fileRef.current.click()
          }
        } catch {
          // base64 could not be decoded into a valid file
          showErrorModal({ code: 'CORRUPTED' })
        }
        setNeedDownload(false)
        return
      }
      if (binaryError) {
        // download failed (401/403, 504, etc.)
        showErrorModal(binaryError)
        setNeedDownload(false)
        return
      }
      // completed with no error but no usable binary (empty/invalid payload)
      showErrorModal({ code: 'CORRUPTED' })
      setNeedDownload(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [binaryLoading, binaryPreviewLoading, needDownload]
  )

  const previewPdf = useCallback(
    (e, binaryId) => {
      e.preventDefault()
      const url = `/pdf?module=${binarySearch.name}&id=${binaryId}`
      const newTab = window.open(url, '_blank')

      if (binaryLoading || binaryPreviewLoading) {
        return
      }

      const currentBinary = _.get(binaryCache, binaryId, null)
      if (currentBinary) {
        return
      }

      dispatch(preview(binaryId))
      previewTabRef.current = newTab

      if (previewPollRef.current) {
        clearInterval(previewPollRef.current)
      }
      previewPollRef.current = setInterval(() => {
        if (previewTabRef.current?.closed) {
          clearInterval(previewPollRef.current)
          previewTabRef.current = null
          if (isPreviewLoadingRef.current) {
            dispatch(cancelPreview())
          }
        }
      }, 500)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      binaryLoading,
      binaryPreviewLoading,
      binaryCache,
      preview,
      cancelPreview,
      dispatch
    ]
  )

  const showErrorModal = useCallback(
    error => {
      showErrorModalContent({
        preventClose: true,
        render: ({ hideModal }) => (
          <ErrorModal
            hideModal={hideModal}
            title={t('errorTitle')}
            message={getDownloadErrorMessage(error, t)}
            renderIcon={() => (
              <IconErrorMessages color={'#BDBDBD'} width={60} height={60} />
            )}
            actions={[
              {
                label: 'close',
                onPress: hideModal
              }
            ]}
          />
        )
      })
    },
    [showErrorModalContent, t]
  )

  const fileRef = useRef()
  const viewDownloadHandler = fileId => {
    if (!binaryLoading && !binaryPreviewLoading) {
      const currentBinary = _.get(binaryCache, fileId, null)
      setFileId(fileId)
      if (currentBinary) {
        setCurrBinary(currentBinary)
      } else {
        dispatch(request(fileId))
        setCurrBinary('')
      }
      setNeedDownload(true)
    }
  }

  return (
    <>
      {(binaryLoading || binaryPreviewLoading) && <Loading centerInView />}
      <a ref={fileRef} style={styles.hide} target={'_blank'} rel='noreferrer' />
      {items.map((item, index) => {
        const attachment = _.get(item, 'resource.content', null)
        const pdfAttachment = attachment?.filter(
          file => file?.attachment?.contentType === 'application/pdf'
        )
        const isPdf = !_.isEmpty(pdfAttachment)
        const attachmentUrl = _.get(pdfAttachment, '[0].attachment.url', null)
        const binaryId = _.last(attachmentUrl?.split('/'))
        return isPdf ? (
          <Fragment key={index}>
            <div style={styles.testContainer(isMobile)}>
              {!isMobile && (
                <div style={styles.leftContainer}>
                  <a
                    href={`/pdf?module=${binarySearch.name}&id=${binaryId}`}
                    target={'_blank'}
                    rel='noopener noreferrer'
                    style={styles.link}
                    onClick={e => previewPdf(e, binaryId)}
                  >
                    <Text
                      text={
                        needSubtitle
                          ? _.get(item, 'resource.type.text', '')
                          : _.get(
                              pdfAttachment,
                              '[0].attachment.title',
                              _.get(item, 'resource.type.text', '')
                            )
                      }
                      textStyle={styles.linkText}
                    />
                  </a>
                  <Text
                    style={styles.textLeft}
                    label={t('documentType')}
                    text={_.get(item, 'resource.type.text', '')}
                  />
                  <Text
                    textAlign='right'
                    style={styles.text}
                    label={t('dates')}
                    text={formatDateTime(
                      _.get(
                        pdfAttachment,
                        '[0].attachment.creation',
                        _.get(item, 'resource.date', '')
                      )
                    )}
                  />
                  <Text
                    style={styles.textLeft}
                    label={t('verifyingProvider')}
                    text={_.get(item, 'resource.authenticator.display', '')}
                  />
                </div>
              )}
              {isMobile && (
                <>
                  <div style={styles.leftContainer}>
                    <a
                      href={`/pdf?module=${binarySearch.name}&id=${binaryId}`}
                      target={'_blank'}
                      rel='noopener noreferrer'
                      style={styles.link}
                      onClick={e => previewPdf(e, binaryId)}
                    >
                      <Text
                        text={
                          needSubtitle
                            ? _.get(item, 'resource.type.text', '')
                            : _.get(
                                pdfAttachment,
                                '[0].attachment.title',
                                _.get(item, 'resource.type.text', '')
                              )
                        }
                        textStyle={styles.linkText}
                      />
                    </a>
                    <Text
                      style={styles.textLeft}
                      label={t('documentType')}
                      text={_.get(item, 'resource.type.text', '')}
                    />
                  </div>
                  <div style={styles.leftContainer}>
                    <Text
                      style={styles.textLeft}
                      label={t('dates')}
                      text={formatDateTime(
                        _.get(pdfAttachment, '[0].attachment.creation', '')
                      )}
                    />
                    <Text
                      style={styles.textLeft}
                      label={t('verifyingProvider')}
                      text={_.get(item, 'resource.authenticator.display', '')}
                    />
                  </div>
                </>
              )}
              <div style={styles.buttonContainer(isMobile)}>
                <Button
                  style={styles.buttonDownload.button}
                  onClick={() => viewDownloadHandler(binaryId)}
                >
                  <DownIcon style={styles.buttonIcon} />
                  <span>{t('download')}</span>
                </Button>
              </div>
            </div>
            {index !== items.length - 1 && <Divider />}
          </Fragment>
        ) : null
      })}
    </>
  )
}

export const RadiologyFragment = withLocalization(
  RadiologyFragmentModule,
  localization
)

const Radiology = ({ name, loading, data, error }) => {
  const dispatch = useDispatch()
  const patientId = useSelector(getCurPatientId)
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const items = useMemo(() => {
    return _.get(sortRadiology(data), 'entry', [])
  }, [data])

  const isRadiologyNextLoading = useSelector(radiologySearchNextLoading)
  const [allDataLoaded, setAllDataLoaded] = useState(false)

  const getNextData = () => {
    const hasNext = _.get(data, 'link[1].relation') === 'next'
    if (hasNext) {
      const nextUrl = _.get(data, 'link[1].url', '')
      const params = queryParams(nextUrl)
      const { pageContext, pageDirection } = getPageParamsFromUrl(nextUrl)
      const _count = _.get(params, '_count')
      dispatch(
        FhirEnhancedActions.radiologySearchNext(
          patientId,
          _count ? `_count=${_count}` : undefined,
          pageContext,
          pageDirection
        )
      )
    } else {
      setAllDataLoaded(true)
    }
  }

  useEffect(() => {
    const hasNext = _.get(data, 'link[1].relation') === 'next'
    setAllDataLoaded(!hasNext)
  }, [data])

  return (
    <div style={styles.container}>
      {loading && _.isEmpty(items) && <Loading />}
      {!_.isEmpty(items) && (
        <RadiologyFragment
          binarySearch={binarySearch}
          items={items}
          lableLeft={'radiology'}
        />
      )}
      {!loading &&
        _.isEmpty(items) &&
        (error ? <SthWentWrong /> : <NoData name={name} />)}
      {!allDataLoaded ? (
        <div style={styles.loadMoreButtonWrapper}>
          <LoadMoreButton
            loading={isRadiologyNextLoading}
            onClick={getNextData}
          />
        </div>
      ) : null}
    </div>
  )
}

export default Radiology
